/**
 * Turning what somebody typed — or pasted — into something searchable.
 *
 * **The bug this exists for:** pasting a cart line's own description into the
 * register search box found nothing. `VISTA II · BLACK/INFRARED · Small` is the
 * product name, the colour and the size joined together, and every search in
 * the product was a single `ILIKE '%whole thing%'` against one column at a
 * time. No column holds all three, so the most natural thing an operator can do
 * — copy what is on screen and look it up — was the one thing guaranteed to
 * fail. Typing `vista` worked, which makes it read like a paste bug rather than
 * what it is.
 *
 * Two changes, and both are needed:
 *
 *  1. **Every token must match, not the whole string** — so `kids jacket` finds
 *     `KIDS FLYNN JACKET`, which the client asked for separately.
 *  2. **Against all of the searchable text at once**, colour and size included,
 *     rather than one column at a time.
 *
 * Pure and shared so the register, the product list and the import preview
 * cannot drift into three different ideas of what searching means — staff use
 * all three and would have to learn each one.
 */

/** `%` and `_` are wildcards in LIKE; a customer's name may contain either. */
export function escapeLikeWildcards(value: string): string {
  return value.replace(/([\\%_])/g, '\\$1');
}

/**
 * The most tokens worth honouring.
 *
 * Every token is another `ILIKE` against every candidate row, so an accidental
 * paste of a paragraph should not become a table scan per word. Eight is far
 * more than any real search and well short of pathological.
 */
const MAX_TOKENS = 8;

/**
 * Split a search term into the words that all have to match.
 *
 * Splits on anything that is not a letter or a digit, which handles the
 * separators that actually turn up in this data: spaces, the `·` the cart uses
 * between name, colour and size, the `/` in `BLACK/INFRARED`, hyphens in a SKU,
 * and the punctuation people paste in from a spreadsheet.
 *
 * Single characters are kept — sizes are `S`, `M`, `L`, and a size is exactly
 * the kind of thing somebody searches for.
 */
export function searchTokens(term: string): string[] {
  const parts = term
    .normalize('NFKC')
    .split(/[^\p{L}\p{N}]+/u)
    .filter((part) => part.length > 0)
    .map((part) => part.toLowerCase());

  // Deduplicated: `black black` is one condition, not two identical ones.
  return [...new Set(parts)].slice(0, MAX_TOKENS);
}

/**
 * Does this text contain every token?
 *
 * The client-side half of the same rule, for lists already in the browser — the
 * import preview filters styles this way rather than asking the server.
 */
export function matchesAllTokens(haystack: string, tokens: readonly string[]): boolean {
  if (tokens.length === 0) return true;
  const text = haystack.toLowerCase();
  return tokens.every((token) => text.includes(token));
}
